/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import Highcharts, { Series, SeriesOptionsType } from 'highcharts';
import { last } from 'lodash';
import get from 'lodash/get';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { ChartsConfigKey } from '../../../../../shared/entities/config';
import { InstallationType, NetworkGroup, TerminalTypeLabel } from '../../../../../shared/entities/terminal';
import TerminalHistory from '../../../../../shared/entities/terminalHistory';
import { TerminalPlatformsEnum } from '../../../../../shared/entities/terminalPlatform';
import { RootState } from '../../../../../store';
import { setChartsXAxisMinMax } from '../../../../../store/terminalHistory/actions';
import { OTHER_PROTOCOL_NAME } from '../../../../../store/terminalHistory/reducer';
import {
  getKuTerminalOnAircraft,
  getSelectedTerminalTypeLabelBasedOnTab,
  terminalTypeDisplayNameFromTerminalTypeLabel,
} from '../../../../../utils/TerminalUtilities';
import ChartCustomTooltip, {
  LEGEND_ITEM_ICON_WIDTH,
  StyledLabel,
  StyledSymbolContainer,
} from '../ChartCustomComponents/ChartCustomTooltip';
import ChartCustomTooltipSymbol from '../ChartCustomComponents/ChartCustomTooltipSymbol';
import { CHART_HEIGHT, CHART_WRAPPER_HEIGHT_PX } from '../ChartsContainerWrapper';
import { ChartSeriesMetadata, ChartType } from '../ChartSeriesItems';
import NoDataAvailablePlaceholder from './NoDataAvailablePlaceholder';
import { SEC_TO_MS_CONVERSION } from '../../../../../utils/DateTimeUtils';

export const SECONDARY_HISTORY_SUFFIX = '-secondaryTerminalHistory';
export const SEC_TO_MINUTE_CONVERSION = 60;
export const TIMESTAMP_BUFFER = 30;
export const DEFAULT_TIMESTAMP_NO_DATA = -1;
export const MAX_CHART_LEGEND_ITEMS_BEFORE_RESIZE = 6;
export const CHART_LEGEND_ITEM_HEIGHT_PX = 16;
export const DEFAULT_CHART_HEIGHT_PX = 151;
export const LARGE_CHART_HEIGHT_PX = 275;

const LEGEND_WIDTH = 150;
const TOOLTIP_LEFT_OFFSET = 30;

const LegendItem = styled.div`
  display: flex;
  &:hover {
    color: #ffffff;
    cursor: pointer;
  }
`;
const StyledLegendItemLabel = styled(StyledLabel)`
  font-size: 14px;
  line-height: 14px;

  width: ${LEGEND_WIDTH - LEGEND_ITEM_ICON_WIDTH}px;
`;
export const defaultChartOptions = (type: ChartType, hasViewAll?: boolean): Highcharts.Options => ({
  chart: {
    type: type,
    height: CHART_WRAPPER_HEIGHT_PX - 34,
    className: 'ftv__highcharts__chart--container',
    marginLeft: 120,
    marginRight: 250,
    styledMode: true,
    events: {
      load: function () {
        const chart = this;
        /**Add legend title after plotting the chart is completed.*/
        if (hasViewAll) {
          setTimeout(function () {
            chart.legend.update({
              title: {
                text: 'Top protocols (current)',
              },
            });
          }, 0);
        }
      },
    },
    // @ts-ignore
    zoomType: 'x' as 'x',
  },

  plotOptions: {
    series: {
      pointPlacement: 'on',
      animation: false,
      marker: {
        enabled: false, // turns off the points on the chart
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  },
  tooltip: {
    hideDelay: 0,
    borderWidth: 0,
    borderColor: 'rgba(0,0,0,0)',
    borderRadius: 8,
    pointFormatter: function () {
      return `${this.y ? this.y.toLocaleString() : ''} ${get(this, 'series.tooltipOptions.valueSuffix', '')}`;
    },
    formatter: getCustomTerminalHistoryTooltip,
    positioner: tooltipLeftPositioner,
    outside: true, // https://api.highcharts.com/highcharts/tooltip.outside
    useHTML: true,
    shadow: false,
    shared: true,
    padding: -5,
    followPointer: true,
    followTouchMove: true,
  },
  title: {
    text: undefined,
  },
  xAxis: {
    type: 'datetime' as 'datetime',
    dateTimeLabelFormats: {
      millisecond: '%H:%M',
      second: '%H:%M',
      minute: '%H:%M',
      hour: '%H:%M',
      day: '%H:%M',
      week: '%H:%M',
      month: '%H:%M',
      year: '%H:%M',
    },
    tickmarkPlacement: 'on',
    maxPadding: 0,
    minPadding: 0,
    minRange: 1000,
    minTickInterval: 60000, // minimum interval between ticks on x axis is 1 minute
    tickPixelInterval: 100, // number of px between ticks
    tickLength: 0, // hide axis ticks
  },
  legend: {
    width: LEGEND_WIDTH,
    layout: 'vertical' as 'vertical',
    align: 'right' as 'right',
    verticalAlign: 'middle' as 'middle',
    symbolHeight: 0.1,
    symbolPadding: 0,
    symbolRadius: 0,
    symbolWidth: 0.1,
    useHTML: true,
    y: hasViewAll ? -30 : 0,

    labelFormatter: function () {
      return renderToStaticMarkup(
        <LegendItem>
          <StyledSymbolContainer>
            {this.options.custom?.useDetailedLabelSymbols ? (
              <ChartCustomTooltipSymbol
                chartType={(this as Series).options.type as ChartType}
                className={this.options.className ? this.options.className : ''}
              />
            ) : (
              <Box sx={{ paddingLeft: '6px', paddingTop: '2px' }}>
                <svg viewBox="0 0 12 12" stroke="none" className={this.options.className}>
                  <circle r={6} cx={6} cy={6} stroke="none" stroke-width="0"></circle>
                </svg>
              </Box>
            )}
          </StyledSymbolContainer>
          <StyledLegendItemLabel>{last(this.name.split('/'))}</StyledLegendItemLabel>
        </LegendItem>,
      );
    },
  },
  credits: {
    enabled: false,
  },
});

/**
 * The default function to form the structure of the data point provided to Highcharts. The point format used by
 * default is [x, y] where x is the timestamp and y is the value for the point.
 * @param timestamp x value of the chart data point
 * @param value y value of the chart data point
 */
const defaultDataFormatter = (
  timestamp: number,
  value: number | null,
  terminalHistoryDataPoint: TerminalHistory | null,
) => {
  return [timestamp, value];
};

// custom hook for getting previous value
function usePrevious<ValueType>(value: ValueType): ValueType | null {
  const ref = useRef<ValueType | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// returns true if the value is within the series min and max values
export const isValidValue = (valueToCheck: number, seriesMetadata: ChartSeriesMetadata) => {
  // value is less than the valid minimum
  if (seriesMetadata.min !== undefined && valueToCheck < seriesMetadata.min) {
    return false;
  } else if (seriesMetadata.max !== undefined && valueToCheck > seriesMetadata.max) {
    // value is greater than the valid maximum
    return false;
  }

  return true;
};

/**
 * Sort the terminal history keys by the value of a given terminal history data point. The greatest value will be first in the list.
 * @param dataPoint the terminal history data point to determine which series has the greatest value
 * @returns the sort function for the terminal history keys that represent the series of the chart.
 */
export const sortSeriesKeysByDataPoint = (dataPoint: TerminalHistory, valueKey: string) => {
  return (a: string, b: string) => {
    const aData = dataPoint[valueKey] ? dataPoint[valueKey][a] : undefined;
    const bData = dataPoint[valueKey] ? dataPoint[valueKey][b] : undefined;

    if (a && a.toLowerCase() === OTHER_PROTOCOL_NAME) {
      return 1;
    }

    if (b && b.toLowerCase() === OTHER_PROTOCOL_NAME) {
      return -1;
    }

    if (!aData && !bData) {
      return 0;
    }

    if (!aData) {
      return 1;
    }

    if (!bData) {
      return -1;
    }

    return bData - aData;
  };
};

/**
 * Returns the terminal type as a string or an empty string if hidden or undefined.
 * @param terminalType the terminal type enum to be converted into a string
 * @param hideLabel whether to hide the suffix
 */
export const getSeriesNameSuffix = (terminalType: TerminalTypeLabel | undefined, hideLabel = false) => {
  if (hideLabel) {
    return '';
  }

  const label = terminalTypeDisplayNameFromTerminalTypeLabel(terminalType);

  return isNil(label) ? '' : `${label} `;
};

/**
 * Determine whether or not to show the chart series for the provided terminal type label
 * based on whether it matches the currently selected terminal tab's terminal type label.
 *
 * @param selectedTerminalTypeLabel the selected terminal tab's terminal type label - it will be undefined if there are no tabs
 * @param terminalType the terminal type whose series visibility should be checked
 */
const shouldShowTerminalTypeLabelSeries = (
  selectedTerminalTypeLabel: TerminalTypeLabel | undefined,
  terminalType: TerminalTypeLabel | undefined,
  acceptedTerminalTypes?: TerminalTypeLabel[],
) => {
  const terminalTypeAccepted = acceptedTerminalTypes
    ? acceptedTerminalTypes.includes(!isNil(terminalType) ? terminalType : -1)
    : true;
  return (
    terminalTypeAccepted && (!isNil(selectedTerminalTypeLabel) ? selectedTerminalTypeLabel === terminalType : true)
  );
};

const getSeriesObj = (
  termHistoryKeys: ChartSeriesMetadata,
  chartType: ChartType,
  terminalTypeLabel: TerminalTypeLabel | undefined,
  seriesKey: string,
  seriesId: string,
  useAlternateSeriesClassName: boolean,
  seriesIndex: number,
) => {
  const tempSeriesData:
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesXrangeOptions
    | Highcharts.SeriesSplineOptions
    | Highcharts.SeriesScatterOptions
    | Highcharts.SeriesColumnOptions = {
    type: get(termHistoryKeys, 'type', chartType),
    name: `${getSeriesNameSuffix(terminalTypeLabel, termHistoryKeys.hideTerminalTypeOnLabel)}${
      typeof termHistoryKeys.label === 'function' ? termHistoryKeys.label(seriesKey) : termHistoryKeys.label
    }`,
    id: seriesId,
    tooltip: { valueSuffix: ` ${termHistoryKeys.units}` },
    className:
      useAlternateSeriesClassName && termHistoryKeys.alternateClassName
        ? termHistoryKeys.alternateClassName
        : Array.isArray(termHistoryKeys.className)
        ? termHistoryKeys.className[seriesIndex]
        : termHistoryKeys.className, // Note: undefined will default to the highcharts default classname
    yAxis: termHistoryKeys.yAxisKey ? termHistoryKeys.yAxisKey : 0, // if no y axis key then return the default of 0
    data: [],
  };
  return tempSeriesData;
};

/**
 * Uses the terminal history data in order to add series/data to the chart and update the data on changes.
 * @param chart the chart to add the series and data to
 * @param hideData whether the series data should be placed on the chart
 * @param useAlternateSeriesClassName use the alternate class name for a series if present in the configuration
 * @param terminalHistory the terminal history data to add and update for the chart
 * @param addGapsToChart the function to add gaps into the chart when necessary
 * @param dataPointFormatter the formatting function for the chart data
 * @param lastTimestampSeriesObj the last timestamp for the different series to help determine when a gap should be added
 * @param gapAdded reference value for whether a gap was added on the last piece of data or not
 * @param shouldRedraw reference value for whether the chart should be redrawn or not
 * @param termHistoryKeysForChart the configuration data for the chart series
 * @param chartType the chart type (i.e. line, bar, etc.)
 * @param terminalTypeLabel the terminal type label that will be added to the series labels based on the configuration
 * @param seriesIdSuffix the suffix to add to the series
 * @param visible whether the series data is visible (data exists in the chart but is not visible)
 */
const useTerminalHistoryToUpdateSeriesData = (
  chart: Highcharts.Chart | null,
  hideData: boolean,
  useAlternateSeriesClassName: boolean,
  terminalHistory: { [key: number]: TerminalHistory },
  addGapsToChart: (
    seriesArray:
      | Array<number | [number | string, number | null] | null | Highcharts.PointOptionsObject>
      | null
      | undefined,
    series: Highcharts.Series | null,
    newDataTimestamp: number,
    lastDataTimestamp: number,
    terminalHistory: { [sourceTime: string]: TerminalHistory },
  ) => void,
  dataPointFormatter: Function,
  lastTimestampSeriesObj: { current: { [key: string]: number } },
  gapAdded: { current: boolean },
  shouldRedraw: { current: boolean },
  termHistoryKeysForChart: ChartSeriesMetadata[],
  chartType: ChartType,
  chartSize: CHART_HEIGHT,
  terminalTypeLabel: TerminalTypeLabel | undefined,
  visible: boolean,
  seriesIdSuffix?: string,
  refreshChartOnNewData?: boolean,
  sortSeriesOnNewData?: boolean,
) => {
  const [isInitialChartDataLoaded, setIsInitialChartDataLoaded] = useState(false);
  const previousTerminalHistory = usePrevious(terminalHistory);

  const getSeriesId = useCallback(
    (key: string) => {
      return seriesIdSuffix ? `${key}${seriesIdSuffix}` : key;
    },
    [seriesIdSuffix],
  );

  const chartHeightPx = useMemo(
    () => (chartSize === CHART_HEIGHT.LARGE ? LARGE_CHART_HEIGHT_PX : DEFAULT_CHART_HEIGHT_PX),
    [chartSize],
  );

  useEffect(() => {
    if (chart) {
      if (chart.legend && chart.legend.allItems.length > MAX_CHART_LEGEND_ITEMS_BEFORE_RESIZE) {
        // Highcharts defaults the item height to 16px for legend items. The Types for highcharts are not up to date so lodash's get
        // is used to get the item height          ;
        chart.setSize(
          undefined,
          chartHeightPx +
            (chart.legend.allItems.length - MAX_CHART_LEGEND_ITEMS_BEFORE_RESIZE) * CHART_LEGEND_ITEM_HEIGHT_PX,
        );
      } else {
        chart.setSize(undefined, chartHeightPx);
      }
    }
  }, [isInitialChartDataLoaded, chart, chartHeightPx]);

  const loadTermHistoryData = useCallback(() => {
    if (chart && !hideData) {
      const sortedTerminalHistoryTimestamps = Object.keys(terminalHistory).sort(
        (a, b) => terminalHistory[a].sourceTime - terminalHistory[b].sourceTime,
      );

      // go through all terminal history values that need to be added to the chart
      // one data point equals one series for the chart
      termHistoryKeysForChart.forEach((termHistoryKeys: ChartSeriesMetadata) => {
        // Filter out any data points that do not have the field associated with this data series.
        // For example: if the chart series is for azimuth degree in the AntennaPointingAnglesChart, filter out any data
        // points that do not have the azimuthDeg field.
        const sortedAndFilteredTimestamps = sortedTerminalHistoryTimestamps.filter((timestamp: string) => {
          const entryToCheck = terminalHistory[timestamp];
          return !isNil(get(entryToCheck, termHistoryKeys.key));
        });
        let sortedSeriesKeys = [termHistoryKeys.key];
        if (termHistoryKeys.dynamicData) {
          let sortFunc;

          if (sortSeriesOnNewData) {
            const mostRecentTimetamp = sortedAndFilteredTimestamps[sortedAndFilteredTimestamps.length - 1];
            const mostRecentDataPoint = terminalHistory[mostRecentTimetamp];

            sortFunc = sortSeriesKeysByDataPoint(mostRecentDataPoint, termHistoryKeys.key);
          }

          sortedSeriesKeys = sortedAndFilteredTimestamps
            .reduce((accumKeys: string[], currentTimestamp: string) => {
              const histData = get(terminalHistory[currentTimestamp], termHistoryKeys.key);

              const keysNotIncluded = Object.keys(histData).filter(val => !accumKeys.includes(val));

              return accumKeys.concat(keysNotIncluded);
            }, [])
            .sort(sortFunc);
        }

        sortedSeriesKeys.forEach((seriesKey: string, idx: number) => {
          const seriesId = getSeriesId(seriesKey);
          // go through all terminal history values that need to be added to the chart
          // one data point equals one series for the chart
          const tempSeriesData = getSeriesObj(
            termHistoryKeys,
            chartType,
            terminalTypeLabel,
            seriesKey,
            seriesId,
            useAlternateSeriesClassName,
            idx,
          );

          lastTimestampSeriesObj.current[seriesId] = DEFAULT_TIMESTAMP_NO_DATA;
          gapAdded.current = false;

          const dataToAddForSeries: Array<number | [number | string, number | null]> = [];
          // loop through each data point for the series and add the values/gaps
          sortedAndFilteredTimestamps.forEach((terminalHistoryTimestampKey: string) => {
            const terminalHistoryTimestampKeyInt = parseInt(terminalHistoryTimestampKey);
            const entry = terminalHistory[terminalHistoryTimestampKeyInt];
            let entryValueFromKey = get(
              entry,
              termHistoryKeys.dynamicData ? `${termHistoryKeys.key}.${seriesKey}` : termHistoryKeys.key,
            );
            if (entryValueFromKey !== undefined) {
              let valueToAdd = isValidValue(entryValueFromKey, termHistoryKeys) ? entryValueFromKey : null;
              addGapsToChart(
                dataToAddForSeries,
                null,
                terminalHistoryTimestampKeyInt,
                lastTimestampSeriesObj.current[seriesId],
                terminalHistory,
              );
              let dataToAdd = dataPointFormatter(terminalHistoryTimestampKeyInt * SEC_TO_MS_CONVERSION, valueToAdd);

              if (dataToAdd) {
                dataToAddForSeries.push(dataToAdd);
                lastTimestampSeriesObj.current[seriesId] = terminalHistoryTimestampKeyInt;
              }

              if (valueToAdd !== null) {
                gapAdded.current = false;
              } else if (dataToAdd) {
                gapAdded.current = true;
              }
            }
          });
          tempSeriesData.data = dataToAddForSeries;

          chart.addSeries(tempSeriesData, false);
        });
      });

      shouldRedraw.current = true;
      return true;
    }
    return false;
  }, [
    terminalHistory,
    chart,
    addGapsToChart,
    dataPointFormatter,
    gapAdded,
    shouldRedraw,
    lastTimestampSeriesObj,
    termHistoryKeysForChart,
    chartType,
    getSeriesId,
    terminalTypeLabel,
    hideData,
    useAlternateSeriesClassName,
    sortSeriesOnNewData,
  ]);

  // add initial data to the chart in the case the chart doesn't refresh for new terminal history data
  useEffect(() => {
    if (!isInitialChartDataLoaded && !refreshChartOnNewData) {
      const isLoaded = loadTermHistoryData();
      setIsInitialChartDataLoaded(isLoaded);
    }
  }, [isInitialChartDataLoaded, refreshChartOnNewData, loadTermHistoryData, setIsInitialChartDataLoaded]);

  // refresh the chart with the new terminal history data
  useEffect(() => {
    if (chart && refreshChartOnNewData) {
      // remove the series from the chart to refresh the data if new data is detected
      if (
        isInitialChartDataLoaded &&
        !isNil(previousTerminalHistory) &&
        !isNil(terminalHistory) &&
        Object.keys(previousTerminalHistory).length !== Object.keys(terminalHistory).length
      ) {
        lastTimestampSeriesObj.current = {};
        while (chart.series.length) {
          chart.series[0].remove(false);
        }
        loadTermHistoryData();
      } else if (!isInitialChartDataLoaded) {
        const isLoaded = loadTermHistoryData();
        setIsInitialChartDataLoaded(isLoaded);
      }
    }
  }, [
    refreshChartOnNewData,
    loadTermHistoryData,
    chart,
    lastTimestampSeriesObj,
    previousTerminalHistory,
    terminalHistory,
    isInitialChartDataLoaded,
  ]);

  // add point/s to chart if needed
  // do not update charts with new points if the entire chart updates with new data
  useEffect(() => {
    if (
      !isNil(previousTerminalHistory) &&
      !isNil(terminalHistory) &&
      chart &&
      isInitialChartDataLoaded &&
      !refreshChartOnNewData
    ) {
      const prevTermHistKeys = Object.keys(previousTerminalHistory);
      const currentTermHistKeys = Object.keys(terminalHistory);

      // new points added to the terminal history data
      if (prevTermHistKeys.length !== currentTermHistKeys.length) {
        const newTimestampsToAdd = currentTermHistKeys.filter(timestamp => !prevTermHistKeys.includes(timestamp));

        if (newTimestampsToAdd && newTimestampsToAdd.length) {
          let newestTimestamp = { ...lastTimestampSeriesObj.current };

          newTimestampsToAdd.forEach((timestamp: string) => {
            const timestampInt = parseInt(timestamp);
            const terminalHistoryDataPoint = terminalHistory[timestampInt];

            termHistoryKeysForChart.forEach((termHistoryKeys: ChartSeriesMetadata, index: number) => {
              if (chart) {
                let seriesKeys = [termHistoryKeys.key];

                if (termHistoryKeys.dynamicData) {
                  seriesKeys = Object.keys(get(terminalHistoryDataPoint, termHistoryKeys.key, {}));
                }

                seriesKeys.forEach((key: string) => {
                  const seriesId = getSeriesId(key);

                  // get series by id and add data point
                  let series: Highcharts.Series = chart.get(seriesId) as Highcharts.Series;
                  const termHistoryDataPointValueFromKey = get(
                    terminalHistoryDataPoint,
                    termHistoryKeys.dynamicData ? `${termHistoryKeys.key}.${key}` : termHistoryKeys.key,
                  );

                  if (termHistoryDataPointValueFromKey !== undefined) {
                    let valueToAdd = isValidValue(termHistoryDataPointValueFromKey, termHistoryKeys)
                      ? termHistoryDataPointValueFromKey
                      : null;
                    let dataToAdd = dataPointFormatter(timestampInt * SEC_TO_MS_CONVERSION, valueToAdd);

                    if (series) {
                      addGapsToChart(null, series, timestampInt, newestTimestamp[seriesId], terminalHistory);
                    } else if (termHistoryKeys.dynamicData) {
                      const tempSeries = getSeriesObj(
                        termHistoryKeys,
                        chartType,
                        terminalTypeLabel,
                        key,
                        seriesId,
                        useAlternateSeriesClassName,
                        index,
                      );
                      series = chart.addSeries(tempSeries, false);
                    }

                    if (dataToAdd && series) {
                      series.addPoint(dataToAdd, false);
                      newestTimestamp[seriesId] = timestampInt;
                      shouldRedraw.current = true;

                      if (valueToAdd !== null) {
                        gapAdded.current = false;
                      } else {
                        gapAdded.current = true;
                      }
                    }
                  }
                });
              }
            });
          });

          lastTimestampSeriesObj.current = newestTimestamp;
        }
      }
    }
  }, [
    chart,
    previousTerminalHistory,
    terminalHistory,
    addGapsToChart,
    isInitialChartDataLoaded,
    dataPointFormatter,
    gapAdded,
    shouldRedraw,
    lastTimestampSeriesObj,
    termHistoryKeysForChart,
    getSeriesId,
    chartType,
    terminalTypeLabel,
    useAlternateSeriesClassName,
    refreshChartOnNewData,
  ]);

  // toggle the series visibility as long as it's not a dynamic series chart
  useEffect(() => {
    termHistoryKeysForChart.forEach((termHistoryKeys: ChartSeriesMetadata) => {
      if (chart && isInitialChartDataLoaded && !termHistoryKeys.dynamicData) {
        const seriesId = getSeriesId(termHistoryKeys.key);
        const series: Highcharts.Series = chart.get(seriesId) as Highcharts.Series;
        if (series && series.visible !== visible) {
          series.update({ visible: visible, showInLegend: visible } as SeriesOptionsType, false);
          shouldRedraw.current = true;
        }
      }
    });
  }, [chart, termHistoryKeysForChart, visible, shouldRedraw, getSeriesId, isInitialChartDataLoaded]);

  useEffect(() => {
    if (chart) {
      termHistoryKeysForChart.forEach((termHistoryKeys: ChartSeriesMetadata) => {
        // cannot change the series name for a dynamic chart
        if (!termHistoryKeys.dynamicData) {
          const seriesId = getSeriesId(termHistoryKeys.key);

          const series = chart.get(seriesId) as Highcharts.Series;

          if (series) {
            series.update(
              // @ts-ignore
              {
                name: `${getSeriesNameSuffix(terminalTypeLabel, termHistoryKeys.hideTerminalTypeOnLabel)}${
                  termHistoryKeys.label
                }`,
              },
              false,
            );
            shouldRedraw.current = true;
          }
        }
      });
    }
  }, [chart, termHistoryKeysForChart, getSeriesId, terminalTypeLabel, shouldRedraw]);

  return isInitialChartDataLoaded;
};

/**
 * Wrapper class for a highchart line chart that pulls  data from the terminalHistory piece of redux.
 * This higher order component (HOC) initializes the highcharts chart and passes it down to the wrapped
 * component for further customization.
 *
 * Initialization of chart data:
 * Converts data in redux from map to highcharts data type.
 * Ex:
 * {
 *    timestamp1: {
 *      dataType1: data1,
 *      ...
 *    },
 *    timestamp2: {
 *      dataType1: data2,
 *      ...
 *    }
 *    ...
 * }
 * converts to =>
 * [[timestamp1, data1], [timestamp2, data2]...]
 *
 * Note: dataType1 in the example above is passed to the HOC in the termHistoryKeysForChart parameter.
 *
 * Data updates using componentDidUpdate with new data from the terminalHistory store:
 * Steps performed:
 *  1) Are there more data points in the terminalHistory store that have not been rendered?
 *  2) If yes, get only the new data points
 *  3) TODO: Determine if there are any gaps between the new points and the old points
 *  4) TODO: If yes, add extra null data points to ensure gaps are rendered in the charts
 *  5) Pass the new data points and any gap data points to addPoint() https://api.highcharts.com/class-reference/Highcharts.Series#addPoint
 *
 * Parameters:
 *  ChartComponent - the specific React Component that is being wrapped by this functionality.
 *  termHistoryKeysForChart - the keys from the TerminalHistory interface that will be pulled from the store
 *                            and rendered as an individual series in the highcharts chart. Note that these keys follow the
 *                            use of the lodash get function path parameter: https://lodash.com/docs/4.17.15#get. I.e. if you have
 *                            an object { 'a': { 'b': 1 }} then you can get 1 by passing 'a.b' as a parameter.
 */
export const withTerminalHistoryChartHOC = (
  ChartComponent: React.ComponentType<any>,
  chartType: ChartType,
  chartOptions: Highcharts.Options,
  termHistoryKeysForChart: ChartSeriesMetadata[],
  chartGapKey: ChartsConfigKey,
  chartSize: CHART_HEIGHT,
  refreshChartOnNewData = false, // this is used in the case that the historical data can change when new data comes in
  sortSeriesOnNewData = false,
  dataPointFormatter: Function = defaultDataFormatter,
) => {
  interface HOCProps extends StateProps, DispatchProps {
    onlyUseSecondaryTerminalHistory?: boolean;
    onlyUsePrimaryTerminalHistory?: boolean;
    primaryTerminalHistory: { [key: number]: TerminalHistory };
    secondaryTerminalHistory: { [key: number]: TerminalHistory };
    primaryTerminalTypeLabel: TerminalTypeLabel | undefined;
    secondaryTerminalTypeLabel: TerminalTypeLabel | undefined;
    showPrimarySeries: boolean;
    showSecondarySeries: boolean;
    isUsingOtherTerminalHistory: boolean;
  }

  function TerminalHistoryHOC(props: HOCProps) {
    const {
      selectedTerminalTabIndex,
      setChartsXAxisMinMax,
      chartsXMin,
      chartsXMax,
      chartsXMinZoom,
      chartsXMaxZoom,
      chartGapSeconds,
      onlyUseSecondaryTerminalHistory,
      onlyUsePrimaryTerminalHistory,
      primaryTerminalHistory,
      secondaryTerminalHistory,
      primaryTerminalTypeLabel,
      secondaryTerminalTypeLabel,
      showPrimarySeries,
      showSecondarySeries,
      isUsingOtherTerminalHistory,
    } = props;

    const container = useRef<HTMLDivElement | null>(null);

    // using refs since the change of these values will not cause re-renders
    // these are used for reference throughout the logic in the component
    const lastTimestampSeriesObj = useRef<{ [key: string]: number }>({});
    const gapAdded = useRef(false);
    const shouldRedraw = useRef(false);
    const latestPrimaryTerminalHistoryRef = useRef(primaryTerminalHistory);
    const latestSecondaryTerminalHistoryRef = useRef(secondaryTerminalHistory);

    // Keep terminal history refs in sync with latest prop values on every render
    latestPrimaryTerminalHistoryRef.current = primaryTerminalHistory;
    latestSecondaryTerminalHistoryRef.current = secondaryTerminalHistory;

    const [chart, setChart] = useState<Highcharts.Chart | null>(null);

    const setChartExtremes = useCallback(
      function (setExtremesEventObject: Highcharts.AxisSetExtremesEventObject) {
        setChartsXAxisMinMax(setExtremesEventObject.min, setExtremesEventObject.max);

        // @ts-ignore
        const { chart } = this;

        // the chart zoom was reset to default
        if (!setExtremesEventObject.min && !setExtremesEventObject.max) {
          if (chart) {
            // @ts-ignore
            let resetZoomButton = chart.resetZoomButton;

            // destroy all reset zoom buttons
            if (resetZoomButton) {
              // @ts-ignore
              chart.resetZoomButton = resetZoomButton.destroy();
            }
          }
        }
      },
      [setChartsXAxisMinMax],
    );

    // function to add gaps to a chart
    const addGapsToChart = useCallback(
      (
        seriesArray:
          | Array<number | [number | string, number | null] | null | Highcharts.PointOptionsObject>
          | null
          | undefined,
        series: Highcharts.Series | null,
        newDataTimestamp: number,
        lastDataTimestamp: number,
        terminalHistory: { [sourceTime: string]: TerminalHistory },
      ) => {
        let tempTimestamp = lastDataTimestamp;

        // do not add gap to the start of the graph and not if the gap was already added
        if (tempTimestamp !== DEFAULT_TIMESTAMP_NO_DATA && !gapAdded.current) {
          // Check if the entry has a dynamic chart gap and use it for the chart gap else use the configured default chart gap
          // from the insights java application config
          let gapSeconds = chartGapSeconds;
          if (
            terminalHistory[newDataTimestamp] &&
            terminalHistory[newDataTimestamp][chartGapKey] &&
            !isNil(terminalHistory[newDataTimestamp][chartGapKey])
          ) {
            gapSeconds = terminalHistory[newDataTimestamp][chartGapKey];
          }
          // add gap to the chart in the case that the last message received was more than a minute prior
          if (newDataTimestamp - tempTimestamp > gapSeconds + TIMESTAMP_BUFFER) {
            let nullPoint = dataPointFormatter(tempTimestamp * SEC_TO_MS_CONVERSION, null);

            if (seriesArray && nullPoint) {
              seriesArray.push(nullPoint);
              gapAdded.current = true;
            }

            if (series && nullPoint) {
              series.addPoint(nullPoint, false);
              gapAdded.current = true;
              shouldRedraw.current = true;
            }
          }
        }
      },
      [chartGapSeconds, shouldRedraw],
    );

    useEffect(() => {
      // only run this on initial load
      if (!chart) {
        if (container && container.current) {
          if (chartOptions.xAxis) {
            (chartOptions.xAxis as Highcharts.XAxisOptions).crosshair = {
              color: 'rgba(255,255,255,0.50)',
              dashStyle: 'Solid',
              snap: true,
              width: 1,
              zIndex: 2,
              className: 'ftv__highcharts__chart--crosshair',
            };
            (chartOptions.xAxis as Highcharts.XAxisOptions).events = {
              setExtremes: setChartExtremes,
            };
          }

          // @ts-ignore
          chartOptions.plotOptions = {
            ...chartOptions.plotOptions,
            series: {
              ...get(chartOptions, 'plotOptions.series'),
              custom: {
                ...get(chartOptions, 'plotOptions.series.custom'),
                // primaryTerminalHistory and secondaryTerminalHistory are passed as function references to refs,
                // as passing the large amount of data directly causes a deep-clone, which slows down performance with multiple renders
                getPrimaryTerminalHistory: () => latestPrimaryTerminalHistoryRef.current,
                getSecondaryTerminalHistory: () => latestSecondaryTerminalHistoryRef.current,
                selectedTerminalTabIndex,
                isUsingOtherTerminalHistory,
                onlyUsePrimaryTerminalHistory,
                onlyUseSecondaryTerminalHistory,
              },
            },
          };
          let newChart = Highcharts.chart(container.current, chartOptions);

          setChart(newChart);
        }
      }
    }, [
      lastTimestampSeriesObj,
      setChartExtremes,
      chart,
      addGapsToChart,
      container,
      selectedTerminalTabIndex,
      isUsingOtherTerminalHistory,
      onlyUsePrimaryTerminalHistory,
      onlyUseSecondaryTerminalHistory,
    ]);

    // tear down the chart when unmounting
    useEffect(() => {
      return () => {
        if (chart) {
          chart.destroy();
          setChart(null);
        }
      };
    }, [chart]);

    // Update primary terminal's series
    useTerminalHistoryToUpdateSeriesData(
      chart,
      !!onlyUseSecondaryTerminalHistory,
      false,
      primaryTerminalHistory,
      addGapsToChart,
      dataPointFormatter,
      lastTimestampSeriesObj,
      gapAdded,
      shouldRedraw,
      termHistoryKeysForChart,
      chartType,
      chartSize,
      !isNil(primaryTerminalTypeLabel) && !isNil(secondaryTerminalTypeLabel) ? primaryTerminalTypeLabel : undefined,
      showPrimarySeries,
      undefined,
      refreshChartOnNewData,
      sortSeriesOnNewData,
    );

    // Update secondary terminal's series
    useTerminalHistoryToUpdateSeriesData(
      chart,
      !!onlyUsePrimaryTerminalHistory,
      true,
      secondaryTerminalHistory,
      addGapsToChart,
      dataPointFormatter,
      lastTimestampSeriesObj,
      gapAdded,
      shouldRedraw,
      termHistoryKeysForChart,
      chartType,
      chartSize,
      !isNil(primaryTerminalTypeLabel) && !isNil(secondaryTerminalTypeLabel) ? secondaryTerminalTypeLabel : undefined,
      showSecondarySeries,
      SECONDARY_HISTORY_SUFFIX,
      refreshChartOnNewData,
      sortSeriesOnNewData,
    );

    useEffect(() => {
      // sync the chart min and max
      if (!isNil(chartsXMin) && !isNil(chartsXMax)) {
        if (chart && chart.xAxis && chart.xAxis[0]) {
          chart.xAxis[0].update(
            {
              min: chartsXMin * SEC_TO_MS_CONVERSION,
              max: chartsXMax * SEC_TO_MS_CONVERSION,
            },
            false,
          );
          shouldRedraw.current = true;
        }
      }
    }, [chartsXMin, chartsXMax, chart, shouldRedraw]);

    useEffect(() => {
      // sync the chart zoom level
      // if the values are undefined the chart will be set back to its original state
      if (chartsXMinZoom !== null && chartsXMaxZoom !== null) {
        if (chart && chart.xAxis && chart.xAxis[0]) {
          chart.xAxis[0].setExtremes(chartsXMinZoom, chartsXMaxZoom, false, false);
          shouldRedraw.current = true;
        }
      }
    }, [chartsXMinZoom, chartsXMaxZoom, chart]);

    useEffect(() => {
      if (chart) {
        // add the terminal history as a custom variable to highcharts to let the tooltip
        // render the information needed
        chart.update(
          {
            plotOptions: {
              ...chart.options.plotOptions,
              series: {
                ...get(chart.options, 'plotOptions.series'),
                custom: {
                  ...get(chart.options, 'plotOptions.series.custom'),
                  // primaryTerminalHistory and secondaryTerminalHistory are passed as function references to refs,
                  // as passing the large amount of data directly causes a deep-clone, which slows down performance with multiple renders
                  getPrimaryTerminalHistory: () => latestPrimaryTerminalHistoryRef.current,
                  getSecondaryTerminalHistory: () => latestSecondaryTerminalHistoryRef.current,
                  selectedTerminalTabIndex,
                  isUsingOtherTerminalHistory,
                  onlyUsePrimaryTerminalHistory,
                  onlyUseSecondaryTerminalHistory,
                },
              },
            },
          },
          false,
        );
        shouldRedraw.current = true;
      }
    }, [
      chart,
      isUsingOtherTerminalHistory,
      onlyUsePrimaryTerminalHistory,
      onlyUseSecondaryTerminalHistory,
      selectedTerminalTabIndex,
    ]);

    useEffect(() => {
      if (chart && shouldRedraw.current) {
        chart.redraw(false);
        shouldRedraw.current = false;
      }
    });

    return (
      <ChartComponent {...props} chart={chart}>
        <div ref={container} />
      </ChartComponent>
    );
  }

  const mapStateToProps = (state: RootState) => ({
    chartsXMinZoom: state.terminalHistory.chartsXAxisMinZoom,
    chartsXMaxZoom: state.terminalHistory.chartsXAxisMaxZoom,
    chartsXMin: state.terminalHistory.chartsXAxisMin,
    chartsXMax: state.terminalHistory.chartsXAxisMax,
    chartGapSeconds: state.config.appConfig.chartsConfig[chartGapKey],
    terminalTypeLabel: state.terminals.tailDetailsPanelTerminal
      ? state.terminals.tailDetailsPanelTerminal.terminalTypeLabel
      : undefined,
    otherTerminalTypeLabel:
      state.terminals.tailDetailsPanelTerminal &&
      state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft &&
      state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft.length
        ? state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft[0].terminalTypeLabel
        : undefined,
    selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
    tailDetailsPanelTerminal: state.terminals.tailDetailsPanelTerminal,
  });
  type StateProps = ReturnType<typeof mapStateToProps>;

  const mapDispatchToProps = {
    setChartsXAxisMinMax,
  };
  type DispatchProps = typeof mapDispatchToProps;

  return connect(mapStateToProps, mapDispatchToProps)(TerminalHistoryHOC);
};

export const withTerminalHistoryChartHOCAndNoDataAvailableHOC = (
  ChartComponent: React.ComponentType<any>,
  chartType: ChartType,
  chartOptions: Highcharts.Options,
  termHistoryKeysForChart: ChartSeriesMetadata[],
  chartGapKey: ChartsConfigKey,
  chartSize: CHART_HEIGHT = CHART_HEIGHT.DEFAULT,
  refreshChartOnNewData = false, // this is used in the case that the historical data can change when new data comes in
  sortSeriesOnNewData = false,
  dataPointFormatter: Function = defaultDataFormatter,
) => {
  return showChartOrDataAvailableHOC(
    withTerminalHistoryChartHOC(
      ChartComponent,
      chartType,
      chartOptions,
      termHistoryKeysForChart,
      chartGapKey,
      chartSize,
      refreshChartOnNewData,
      sortSeriesOnNewData,
      dataPointFormatter,
    ),
    termHistoryKeysForChart,
  );
};

export interface TerminalHistorySelectors {
  selectTerminalHistory: (state: RootState) => { [key: number]: TerminalHistory };
  selectOtherTerminalHistory: (state: RootState) => { [key: number]: TerminalHistory };
}

const defaultTerminalHistorySelectors: TerminalHistorySelectors = {
  selectTerminalHistory: (state: RootState) => state.terminalHistory.normalizedTerminalHistory,
  selectOtherTerminalHistory: (state: RootState) => state.terminalHistory.normalizedOtherTerminalHistory,
};

/**
 * This higher order component will return the No Data Available label when the chart currently has no information
 * for the given series in the terminalHistory part of the store.
 * Once the data is available the chart higher order component will be mounted.
 * @param ChartHOC the higher order component that encapsulates the chart creation and data from terminal history
 * @param termHistoryKeysForChart the chart series data for this chart
 * @param terminalHistorySelectors reads the terminal histories for the chart, for a chart that derives new values
 *                                 from the terminal history in the store. Each selector must return the same object
 *                                 while its data does not change.
 */
export const showChartOrDataAvailableHOC = (
  ChartHOC: React.ComponentType<any>,
  termHistoryKeysForChart: ChartSeriesMetadata[],
  terminalHistorySelectors: TerminalHistorySelectors = defaultTerminalHistorySelectors,
) => {
  interface DataAvailableHOCProps extends StateProps {
    index: number;
    text: string;
    id: string;
    onlyUseSecondaryTerminalHistory?: boolean;
    onlyUsePrimaryTerminalHistory?: boolean;
    useSelectedKuPlusKaTabTerminalHistory?: boolean;
    showKuOrKaChartNamePrefix?: boolean;
    isRestricted?: boolean;
    alwaysShow?: boolean;
    acceptedTerminalTypes?: TerminalTypeLabel[];
    acceptedPlatformTypes?: TerminalPlatformsEnum[];
    acceptedNetworkTypes?: NetworkGroup[];
  }

  function DataAvailableHOC(props: DataAvailableHOCProps) {
    const {
      terminalHistory,
      isRequestingTerminalHistory,
      otherTerminalHistory,
      onlyUsePrimaryTerminalHistory,
      onlyUseSecondaryTerminalHistory,
      useSelectedKuPlusKaTabTerminalHistory,
      terminalTypeLabel,
      otherTerminalTypeLabel,
      showKuOrKaChartNamePrefix,
      isRestricted,
      alwaysShow,
      selectedTerminalTabIndex,
      tailDetailsPanelTerminal,
      acceptedTerminalTypes,
      acceptedPlatformTypes,
      acceptedNetworkTypes,
    } = props;
    const [isDataAvailable, setDataAvailable] = React.useState(false);
    const [showPrimarySeries, setShowPrimarySeries] = useState(true);
    const [showSecondarySeries, setShowSecondarySeries] = useState(false);
    const [primaryTerminalHistory, setPrimaryTerminalHistory] = useState(terminalHistory);
    const [secondaryTerminalHistory, setSecondaryTerminalHistory] = useState(otherTerminalHistory);
    const [primaryTerminalTypeLabel, setPrimaryTerminalTypeLabel] = useState(terminalTypeLabel);
    const [secondaryTerminalTypeLabel, setSecondaryTerminalTypeLabel] = useState(otherTerminalTypeLabel);
    const [isUsingOtherTerminalHistory, setIsUsingOtherTerminalHistory] = useState(false);

    function findDataInTerminalHistory(key: string, history: { [key: number]: TerminalHistory }) {
      const found = Object.keys(history).find((timestamp: string) => {
        const dataPoint = history[timestamp];
        if (dataPoint && has(dataPoint, key) && !isNil(get(dataPoint, key))) {
          return true;
        }

        return false;
      });

      return !!found;
    }

    // Determine which series to show.
    // Single charts show both Ku and Ka data, so turn on / off series
    function applyKuPlusKaSeriesVisibility(selectedTerminalTypeLabel: TerminalTypeLabel | undefined) {
      if (useSelectedKuPlusKaTabTerminalHistory) {
        // Aircraft-level chart (e.g. speed/altitude): show the primary (Ka) series on the
        // Overview and Ka Ant. tabs, and the secondary (Ku) series on the Ku Ant. tab.
        const showSecondary = selectedTerminalTypeLabel === TerminalTypeLabel.KU;
        setShowPrimarySeries(!showSecondary);
        setShowSecondarySeries(showSecondary);
      } else if (!onlyUsePrimaryTerminalHistory && !onlyUseSecondaryTerminalHistory) {
        setShowPrimarySeries(
          shouldShowTerminalTypeLabelSeries(selectedTerminalTypeLabel, TerminalTypeLabel.KA, acceptedTerminalTypes),
        );
        setShowSecondarySeries(
          shouldShowTerminalTypeLabelSeries(selectedTerminalTypeLabel, TerminalTypeLabel.KU, acceptedTerminalTypes),
        );
      } else if (onlyUseSecondaryTerminalHistory) {
        // Ku only chart shows only the secondary series
        setShowPrimarySeries(false);
        setShowSecondarySeries(true);
      } else if (onlyUsePrimaryTerminalHistory) {
        // Ka only chart shows only the primary series
        setShowPrimarySeries(true);
        setShowSecondarySeries(false);
      } else {
        setShowPrimarySeries(true);
        setShowSecondarySeries(true);
      }
    }

    // Determine which terminal history to use.
    function applyKuPlusKaTerminalHistory() {
      const kuTerminal = getKuTerminalOnAircraft(tailDetailsPanelTerminal);
      if (kuTerminal.datapathUuid === tailDetailsPanelTerminal!.datapathUuid) {
        setPrimaryTerminalHistory(otherTerminalHistory);
        setSecondaryTerminalHistory(terminalHistory);
        setIsUsingOtherTerminalHistory(!!onlyUsePrimaryTerminalHistory);
      } else {
        setPrimaryTerminalHistory(terminalHistory);
        setSecondaryTerminalHistory(otherTerminalHistory);
        setIsUsingOtherTerminalHistory(!!onlyUseSecondaryTerminalHistory);
      }
    }

    useEffect(() => {
      if (tailDetailsPanelTerminal && tailDetailsPanelTerminal.installationType === InstallationType.KU_PLUS_KA) {
        // For Ku + Ka charts with two series of data, this logic
        // sets Ka data to always display as the primary series
        // and Ku data to always display as the secondary series.
        // (e.g. FL/RL Data Rates and Cumulative Data Usage)
        const selectedTerminalTypeLabel = getSelectedTerminalTypeLabelBasedOnTab(
          selectedTerminalTabIndex,
          tailDetailsPanelTerminal,
        );
        setPrimaryTerminalTypeLabel(TerminalTypeLabel.KA);
        setSecondaryTerminalTypeLabel(TerminalTypeLabel.KU);
        applyKuPlusKaSeriesVisibility(selectedTerminalTypeLabel);
        applyKuPlusKaTerminalHistory();
      } else {
        // Default to mapping the currently selected
        // terminal data to the primary chart series
        // for non-Ku+Ka or single-series charts.
        setPrimaryTerminalHistory(terminalHistory);
        setSecondaryTerminalHistory(otherTerminalHistory);
        // check for dual antenna edge case where the other terminal on the platform's history is being used
        if (tailDetailsPanelTerminal && tailDetailsPanelTerminal.installationType === InstallationType.DUAL_TERMINAL) {
          setPrimaryTerminalTypeLabel(selectedTerminalTypeLabel);
        } else {
          setPrimaryTerminalTypeLabel(terminalTypeLabel);
        }
        setSecondaryTerminalTypeLabel(otherTerminalTypeLabel);
      }
    }, [
      acceptedTerminalTypes,
      onlyUsePrimaryTerminalHistory,
      onlyUseSecondaryTerminalHistory,
      useSelectedKuPlusKaTabTerminalHistory,
      selectedTerminalTabIndex,
      tailDetailsPanelTerminal,
      terminalHistory,
      otherTerminalHistory,
      terminalTypeLabel,
      otherTerminalTypeLabel,
      setPrimaryTerminalTypeLabel,
      setSecondaryTerminalTypeLabel,
      setShowPrimarySeries,
      setShowSecondarySeries,
      setPrimaryTerminalHistory,
      setSecondaryTerminalHistory,
    ]);

    useEffect(() => {
      // go through all terminal history values that are in the chart to see if data is available
      const isDataAvailable = termHistoryKeysForChart.reduce((dataFound, chartSeriesInfo) => {
        let primaryDataFound = false;
        let secondaryDataFound = false;

        if (showPrimarySeries || alwaysShow) {
          primaryDataFound = findDataInTerminalHistory(chartSeriesInfo.key, primaryTerminalHistory);
        }

        if (showSecondarySeries || alwaysShow) {
          secondaryDataFound = findDataInTerminalHistory(chartSeriesInfo.key, secondaryTerminalHistory);
        }
        return dataFound || primaryDataFound || secondaryDataFound;
      }, false);
      setDataAvailable(isDataAvailable);
    }, [
      setDataAvailable,
      showPrimarySeries,
      showSecondarySeries,
      primaryTerminalHistory,
      secondaryTerminalHistory,
      alwaysShow,
    ]);

    // Determine whether to render the other terminal chart
    const selectedTerminalTypeLabel = getSelectedTerminalTypeLabelBasedOnTab(
      selectedTerminalTabIndex,
      tailDetailsPanelTerminal,
    );
    const isKuPlusKa = (tailDetailsPanelTerminal || {}).installationType === InstallationType.KU_PLUS_KA;

    // Chart-toggling vars
    // (singleton)
    // e.g. FL data rate chart is 1 chart
    const isSingletonChart = !showKuOrKaChartNamePrefix;
    const selectedTerminalTypeAcceptedForSingletonChart =
      acceptedTerminalTypes && selectedTerminalTypeLabel
        ? acceptedTerminalTypes.includes(selectedTerminalTypeLabel)
        : true;
    const platformTypeAcceptedForSingletonChart =
      acceptedPlatformTypes && tailDetailsPanelTerminal
        ? acceptedPlatformTypes.includes(tailDetailsPanelTerminal.platformType)
        : true;
    const chartIsRestricted = isRestricted && (tailDetailsPanelTerminal || {}).restrictionEnabled;
    // (non-singleton chart vars)
    // e.g. FL traffic protocol charts can be represented by 2 charts
    const selectedTabMatchesPrimaryTerminalType = shouldShowTerminalTypeLabelSeries(
      selectedTerminalTypeLabel,
      primaryTerminalTypeLabel,
    );
    const selectedTabMatchesSecondaryTerminalType = shouldShowTerminalTypeLabelSeries(
      selectedTerminalTypeLabel,
      secondaryTerminalTypeLabel,
    );

    // Determine whether to render the terminal charts
    const showSingletonChart = selectedTerminalTypeAcceptedForSingletonChart && platformTypeAcceptedForSingletonChart;
    const showPrimaryTerminalChart = !isKuPlusKa || selectedTabMatchesPrimaryTerminalType;
    const showSecondaryTerminalChart = isKuPlusKa && selectedTabMatchesSecondaryTerminalType;
    const isChartSupported =
      acceptedNetworkTypes && tailDetailsPanelTerminal
        ? acceptedNetworkTypes.includes(tailDetailsPanelTerminal.networkGroup)
        : true;
    if (
      chartIsRestricted ||
      !isChartSupported ||
      (isSingletonChart && !showSingletonChart) ||
      (!isSingletonChart && onlyUsePrimaryTerminalHistory && !showPrimaryTerminalChart) ||
      (!isSingletonChart && onlyUseSecondaryTerminalHistory && !showSecondaryTerminalChart)
    ) {
      return null;
    }

    let labelPrefix = undefined;

    // charts that have a label prefix are either using exclusively the other terminal history or the current terminal history
    // and the terminal type is Ku or Ka
    if (showKuOrKaChartNamePrefix && isKuPlusKa) {
      labelPrefix = onlyUseSecondaryTerminalHistory ? secondaryTerminalTypeLabel : primaryTerminalTypeLabel;
    }

    if (!isDataAvailable || isRequestingTerminalHistory) {
      return (
        <NoDataAvailablePlaceholder titlePrefixTerminalType={labelPrefix} {...props}>
          <StyledNoDataDiv>No Data Available</StyledNoDataDiv>
        </NoDataAvailablePlaceholder>
      );
    }

    return (
      <ChartHOC
        {...props}
        titlePrefixTerminalType={labelPrefix}
        primaryTerminalHistory={primaryTerminalHistory}
        secondaryTerminalHistory={secondaryTerminalHistory}
        primaryTerminalTypeLabel={primaryTerminalTypeLabel}
        secondaryTerminalTypeLabel={secondaryTerminalTypeLabel}
        showPrimarySeries={showPrimarySeries}
        showSecondarySeries={showSecondarySeries}
        isUsingOtherTerminalHistory={isUsingOtherTerminalHistory}
        onlyUsePrimaryTerminalHistory={onlyUsePrimaryTerminalHistory}
        onlyUseSecondaryTerminalHistory={onlyUseSecondaryTerminalHistory}
      />
    );
  }

  const mapStateToProps = (state: RootState) => ({
    terminalHistory: terminalHistorySelectors.selectTerminalHistory(state),
    otherTerminalHistory: terminalHistorySelectors.selectOtherTerminalHistory(state),
    isRequestingTerminalHistory: state.terminalHistory.isRequestingTerminalHistory,
    terminalTypeLabel: state.terminals.tailDetailsPanelTerminal
      ? state.terminals.tailDetailsPanelTerminal.terminalTypeLabel
      : undefined,
    otherTerminalTypeLabel:
      state.terminals.tailDetailsPanelTerminal &&
      state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft &&
      state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft.length
        ? state.terminals.tailDetailsPanelTerminal.terminalsOnAircraft[0].terminalTypeLabel
        : undefined,
    selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
    tailDetailsPanelTerminal: state.terminals.tailDetailsPanelTerminal,
  });
  type StateProps = ReturnType<typeof mapStateToProps>;

  return connect(mapStateToProps, null)(DataAvailableHOC);
};

const StyledNoDataDiv = styled.div`
  width: 100%;
  opacity: 0.8;
  text-align: center;
  margin-top: 45px;
`;

function getCustomTerminalHistoryTooltip(): string {
  return renderToStaticMarkup(
    // @ts-ignore
    <ChartCustomTooltip data={this} showGraphs={false} sortByYValue={false} />,
  );
}

/**
 * Gets the custom position of the tooltip to put the tooltip on the left
 * of the cursor.
 */
const tooltipLeftPositioner: Highcharts.TooltipPositionerCallbackFunction = function (
  labelWidth: number,
  labelHeight: number,
  point: Highcharts.TooltipPositionerPointObject | Highcharts.Point,
) {
  // @ts-ignore
  const tooltip = this as Highcharts.Tooltip;

  // @ts-ignore
  let chartPosition = tooltip.chart.pointer.chartPosition;

  let x =
    // @ts-ignore
    chartPosition.left - tooltip.distance + point.plotX + tooltip.chart.plotLeft - labelWidth - TOOLTIP_LEFT_OFFSET;
  // @ts-ignore
  let y = chartPosition.top - tooltip.distance + point.plotY + tooltip.chart.plotTop - labelHeight / 2;

  // @ts-ignore
  return { x, y };
};
